import {
    Box,
    Flex,
    Heading,
    Link,
    Stack,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr
} from '@chakra-ui/react';
import Section from 'components/Section';
import Head from 'next/head';
import { BlogPostProps, posts } from '../../components/articles';
import { CTAbuttonSection } from 'components/CTAButton';
import OtherBrowser from 'components/OtherBrowser';
import React from 'react';
import { GetStaticPaths, GetStaticProps } from 'next';

type BlogPost = BlogPostProps;

export default function BlogPost({ post }: { post: BlogPost }) {
    const modifiedDate = post.modifiedDate ?? post.date;

    // Add JSON-LD structured data
    const canonicalUrl = `https://www.tabextend.com/blog/${post.slug}`;
    const structuredData = {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'BlogPosting',
                headline: post.title,
                description: post.description,
                image: `https://www.tabextend.com${post.coverImage}`,
                datePublished: post.date,
                dateModified: modifiedDate,
                author: {
                    '@type': 'Organization',
                    name: 'tabExtend',
                    url: 'https://www.tabextend.com/'
                },
                publisher: {
                    '@type': 'Organization',
                    name: 'tabExtend',
                    logo: {
                        '@type': 'ImageObject',
                        url: 'https://www.tabextend.com/tabExtendWebLogo@2x.png'
                    }
                },
                mainEntityOfPage: {
                    '@type': 'WebPage',
                    '@id': canonicalUrl
                }
            },
            {
                '@type': 'BreadcrumbList',
                itemListElement: [
                    {
                        '@type': 'ListItem',
                        position: 1,
                        name: 'Blog',
                        item: 'https://www.tabextend.com/blog'
                    },
                    {
                        '@type': 'ListItem',
                        position: 2,
                        name: post.title,
                        item: canonicalUrl
                    }
                ]
            }
        ]
    };

    return (
        <>
            <Head>
                <title>{`${post.title} | tabExtend Blog`}</title>
                <meta name="description" content={post?.description} />

                {/* Open Graph tags */}
                <meta property="og:type" content="article" />
                <meta property="og:title" content={post?.title} />
                <meta property="og:description" content={post?.description} />
                <meta property="og:image" content={post?.coverImage} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:site_name" content="tabExtend" />
                <meta name="robots" content="index,follow" />

                {/* Twitter Card tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={post?.title} />
                <meta name="twitter:description" content={post?.description} />
                <meta name="twitter:image" content={post?.coverImage} />

                {/* Article specific meta tags */}
                <meta property="article:published_time" content={post?.date} />
                <meta property="article:modified_time" content={modifiedDate} />

                {/* Canonical URL */}
                <link rel="canonical" href={canonicalUrl} />

                {/* JSON-LD structured data */}
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(structuredData)
                    }}
                />
            </Head>
            <Section
                id="pricing"
                color="white"
                size="small"
                backgroundImage=""
                backgroundImageOpacity={1}
            >
                <div
                    className="container max-w-full px-0"
                    style={{ maxWidth: '786px' }}
                >
                    <div
                        className="relative w-full h-0 pb-[56.25%] mb-8"
                        style={{ borderRadius: '12px', overflow: 'hidden' }}
                    >
                        <img
                            src={post.coverImage}
                            alt={post.title}
                            className="absolute top-0 left-0 w-full h-full object-cover rounded-lg"
                        />
                    </div>
                    <Heading as="h1" size="xl" mb={2}>
                        {post.title}
                    </Heading>
                    <Text mb={6} fontWeight="medium" fontSize="lg">
                        {post.description}
                    </Text>
                    <Text mb={8} fontSize="sm" color="gray.600">
                        Published by tabExtend on{' '}
                        <time dateTime={post.date}>{post.date}</time>
                        {modifiedDate !== post.date && (
                            <>
                                {' '}
                                · Updated{' '}
                                <time dateTime={modifiedDate}>
                                    {modifiedDate}
                                </time>
                            </>
                        )}
                    </Text>
                    {post.content.map((item, index) => {
                        switch (item.type) {
                            case 'text':
                                if (item.links && item.links.length > 0) {
                                    const textContent = item.content;
                                    const parts = textContent
                                        .split(/(\*\*.*?\*\*)/g)
                                        .map((part) => {
                                            if (
                                                part.startsWith('**') &&
                                                part.endsWith('**')
                                            ) {
                                                return (
                                                    <b>{part.slice(2, -2)}</b>
                                                );
                                            }
                                            return part;
                                        });

                                    return (
                                        <Text
                                            key={index}
                                            mb={4}
                                            color="gray.800"
                                        >
                                            {item.links.reduce<
                                                React.ReactNode[]
                                            >(
                                                (content, link) => {
                                                    const textContent = parts
                                                        .map((part) =>
                                                            typeof part ===
                                                            'string'
                                                                ? part
                                                                : part.props
                                                                      .children
                                                        )
                                                        .join('');
                                                    const splitParts =
                                                        textContent.split(
                                                            link.text
                                                        );
                                                    return splitParts.map(
                                                        (part, i) =>
                                                            i ===
                                                            splitParts.length -
                                                                1 ? (
                                                                part
                                                            ) : (
                                                                <React.Fragment
                                                                    key={i}
                                                                >
                                                                    {part}
                                                                    <a
                                                                        href={
                                                                            link.url
                                                                        }
                                                                        style={{
                                                                            textDecoration:
                                                                                'underline'
                                                                        }}
                                                                    >
                                                                        {
                                                                            link.text
                                                                        }
                                                                    </a>
                                                                </React.Fragment>
                                                            )
                                                    );
                                                },
                                                [parts]
                                            )}
                                        </Text>
                                    );
                                } else {
                                    const parts = item.content
                                        .split(/(\*\*.*?\*\*)/g)
                                        .map((part, i) => {
                                            if (
                                                part.startsWith('**') &&
                                                part.endsWith('**')
                                            ) {
                                                return (
                                                    <b key={i}>
                                                        {part.slice(2, -2)}
                                                    </b>
                                                );
                                            }
                                            return part;
                                        });
                                    return (
                                        <Text
                                            key={index}
                                            mb={4}
                                            color="gray.800"
                                        >
                                            {parts}
                                        </Text>
                                    );
                                }
                            case 'h2':
                                return (
                                    <Heading
                                        as="h2"
                                        size="lg"
                                        mb={4}
                                        mt={8}
                                        key={index}
                                    >
                                        {item.content}
                                    </Heading>
                                );
                            case 'h3':
                                return (
                                    <Heading
                                        as="h3"
                                        size="md"
                                        mb={3}
                                        mt={6}
                                        key={index}
                                    >
                                        {item.content}
                                    </Heading>
                                );
                            case 'image':
                                return (
                                    <div
                                        key={index}
                                        className="relative w-full h-0 pb-[56.25%] my-4"
                                    >
                                        <img
                                            src={item.content as string}
                                            alt={`${post.title} illustration`}
                                            className="absolute top-0 left-0 w-full h-full object-cover rounded-lg"
                                        />
                                    </div>
                                );
                            case 'table':
                                return (
                                    <TableContainer
                                        key={index}
                                        my={6}
                                        borderWidth="1px"
                                        borderRadius="lg"
                                    >
                                        <Table variant="simple" size="sm">
                                            <Thead>
                                                <Tr>
                                                    {item.content.headers.map(
                                                        (header) => (
                                                            <Th
                                                                key={header}
                                                                whiteSpace="normal"
                                                            >
                                                                {header}
                                                            </Th>
                                                        )
                                                    )}
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {item.content.rows.map(
                                                    (row, rowIndex) => (
                                                        <Tr key={rowIndex}>
                                                            {row.map(
                                                                (
                                                                    cell,
                                                                    cellIndex
                                                                ) => (
                                                                    <Td
                                                                        key={
                                                                            cellIndex
                                                                        }
                                                                        whiteSpace="normal"
                                                                        verticalAlign="top"
                                                                    >
                                                                        {cell}
                                                                    </Td>
                                                                )
                                                            )}
                                                        </Tr>
                                                    )
                                                )}
                                            </Tbody>
                                        </Table>
                                    </TableContainer>
                                );
                            case 'list':
                                return (
                                    <ul
                                        key={index}
                                        style={{
                                            listStyleType: 'disc',
                                            marginLeft: '1em',
                                            marginBottom: '1em'
                                        }}
                                    >
                                        {(item.content as string[]).map(
                                            (listItem, i) => (
                                                <li key={i}>{listItem}</li>
                                            )
                                        )}
                                    </ul>
                                );
                            case 'numberList':
                                return (
                                    <ol
                                        key={index}
                                        style={{
                                            listStyleType: 'decimal',
                                            marginLeft: '1em',
                                            marginBottom: '1em'
                                        }}
                                    >
                                        {(item.content as string[]).map(
                                            (listItem, i) => {
                                                if (item.links) {
                                                    const link =
                                                        item.links.find(
                                                            (l) =>
                                                                l.text ===
                                                                listItem.trim()
                                                        );
                                                    if (link) {
                                                        return (
                                                            <li
                                                                key={i}
                                                                style={{
                                                                    marginLeft:
                                                                        '1em'
                                                                }}
                                                            >
                                                                <a
                                                                    href={
                                                                        link.url
                                                                    }
                                                                    style={{
                                                                        textDecoration:
                                                                            'underline'
                                                                    }}
                                                                >
                                                                    {listItem.trim()}
                                                                </a>
                                                            </li>
                                                        );
                                                    }
                                                }
                                                return (
                                                    <li
                                                        key={i}
                                                        style={{
                                                            marginLeft: '1em'
                                                        }}
                                                    >
                                                        {listItem.trim()}
                                                    </li>
                                                );
                                            }
                                        )}
                                    </ol>
                                );
                            default:
                                return null;
                        }
                    })}
                    {post.sources && post.sources.length > 0 && (
                        <Box as="section" mt={10} pt={6} borderTopWidth="1px">
                            <Heading as="h2" size="md" mb={3}>
                                Sources
                            </Heading>
                            <Stack as="ul" spacing={2} pl={5}>
                                {post.sources.map((source) => (
                                    <Box as="li" key={source.url}>
                                        <Link
                                            href={source.url}
                                            isExternal
                                            textDecoration="underline"
                                        >
                                            {source.label}
                                        </Link>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    )}
                </div>
                <Flex flexDir="column" alignItems="center">
                    <Text fontSize="20px" fontWeight={'bold'} mb={8} mt={8}>
                        Try tabExtend for free today
                    </Text>
                    <CTAbuttonSection />
                    <OtherBrowser />
                </Flex>
            </Section>
        </>
    );
}

export const getStaticPaths: GetStaticPaths = async () => {
    return {
        paths: posts.map((post) => ({ params: { slug: post.slug } })),
        fallback: false
    };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
    const slug = params?.slug;
    const normalizedSlug = Array.isArray(slug) ? slug[0] : slug;
    const post = posts.find((entry) => entry.slug === normalizedSlug);

    if (!post) {
        return { notFound: true };
    }

    return {
        props: {
            post
        }
    };
};
